import type { Category, RouteById, SuccessOf } from "@volgo/core/contract";
import { type FormEvent, useEffect, useId, useState } from "react";
import { addRuleTags } from "../../search/rule-tags";
import { useSession } from "../../session";
import { Button } from "../../ui/Button";
import { CategoryFilter } from "../../ui/CategoryFilter";
import { ConfirmDialog } from "../../ui/Dialog";
import { describeError } from "../../ui/errors";
import { formatCount, formatDate, formatDateTime } from "../../ui/format";
import { Segmented } from "../../ui/Segmented";
import { Token } from "../../ui/Token";
import { OpsDrawer, type Rule } from "./OpsDrawers";

/** The automatic downloads (discord Spec 6.2): the board's rule list and the drawer that makes and runs a rule. */

type RuleInput = Pick<Rule, "name" | "categories" | "artists" | "tags" | "xtags" | "start">;
type StartMode = Rule["start"]["mode"];
type Preview =
  | { state: "idle" }
  | { state: "loading"; matching: number }
  | { state: "ready"; matching: number | null; notDownloaded: number | null }
  | { state: "error"; error: unknown };

const PHASE_LABELS: Record<Rule["phase"], string> = {
  idle: "꺼짐",
  backfill: "과거 작품 넣는 중",
  follow: "새 작품 따라가는 중",
};
const START_MODES: { value: StartMode; label: string }[] = [
  { value: "relative", label: "최근 N일" },
  { value: "date", label: "날짜 지정" },
];
const RULE_ERRORS = {
  CONFLICT: "켜져 있는 규칙은 고치거나 지울 수 없습니다. 먼저 끄세요.",
  NOT_FOUND: "이미 지운 규칙입니다. 화면을 새로 고쳐 확인하세요.",
};

/** focus after a question: its button is disabled while the answer runs, and a deleted rule's goes away */
const drawerOf = () => document.querySelector<HTMLElement>(".ops-drawer");

function ruleSummary(rule: Rule) {
  const s = rule.stats;
  const tail = `대기 ${formatCount(s.queued)} · 받음 ${formatCount(s.done)}${s.failed ? ` · 실패 ${formatCount(s.failed)}` : ""}`;
  return rule.enabled ? `${PHASE_LABELS[rule.phase]} · ${tail}` : `꺼짐 · ${tail}`;
}

export function RuleSection({
  rules,
  error,
  onOpen,
}: {
  rules: readonly Rule[] | null;
  error: unknown;
  /** null: a new rule */
  onOpen: (id: string | null) => void;
}) {
  return (
    <section className="ops-w" aria-label="자동 받기">
      <header>
        <h2>자동 받기</h2>
        <Button className="ops-r" onClick={() => onOpen(null)}>
          새 규칙
        </Button>
      </header>
      {error !== null && (
        <p className="ops-in note note-warn" role="alert">
          {describeError(error)}
        </p>
      )}
      {rules?.length === 0 && (
        <p className="ops-in note">
          규칙이 없습니다. 규칙을 만들고 켜면 맞는 작품을 누가 읽기 전에 받아 둡니다.
        </p>
      )}
      <ul className="ops-list">
        {rules?.map((rule) => (
          <li key={rule.id}>
            <button type="button" data-rule={rule.id} onClick={() => onOpen(rule.id)}>
              <span className="ops-sw" data-on={rule.enabled} />
              <span>
                {rule.name}
                <small>{ruleSummary(rule)}</small>
              </span>
              <span className="ops-pill" data-tone={rule.enabled ? "run" : "idle"}>
                {rule.enabled ? "켜짐" : "꺼짐"}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** `date` for an `<input type="date">`: midnight of that day in the viewer's time zone. */
function dateStart(date: string): string | null {
  const at = new Date(`${date}T00:00:00`);
  return date && !Number.isNaN(at.getTime()) ? at.toISOString() : null;
}

/**
 * One rule (or a new one): its state and counts, the conditions, the live preview, on/off, cancelling what it
 * queued, deleting. While a rule is on its conditions cannot change (the server answers 409 too).
 */
export function RuleDrawer({
  rule,
  onSaved,
  onChanged,
  onDeleted,
  onShowDownloads,
  onClose,
}: {
  rule: Rule | null;
  onSaved: (rule: Rule) => void;
  /** the rule's queue changed: the board loads it again */
  onChanged: () => void;
  onDeleted: (id: string) => void;
  onShowDownloads: (ruleId: string) => void;
  onClose: () => void;
}) {
  const { client } = useSession();
  const formId = useId();
  const listId = useId();
  const [name, setName] = useState(rule?.name ?? "");
  const [categories, setCategories] = useState<Category[]>(rule?.categories ?? []);
  const [artists, setArtists] = useState<string[]>(rule?.artists ?? []);
  const [tags, setTags] = useState<string[]>(rule?.tags ?? []);
  const [xtags, setXtags] = useState<string[]>(rule?.xtags ?? []);
  const [typed, setTyped] = useState("");
  const [artistError, setArtistError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [mode, setMode] = useState<StartMode>(rule?.start.mode ?? "relative");
  const [days, setDays] = useState(rule?.start.mode === "relative" ? String(rule.start.days) : "7");
  const [date, setDate] = useState(rule?.start.mode === "date" ? formatDate(rule.start.date) : "");
  const [preview, setPreview] = useState<Preview>({ state: "idle" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const [asking, setAsking] = useState<"cancel" | "delete" | null>(null);
  const locked = rule?.enabled ?? false;

  const n = Number(days);
  const start: RuleInput["start"] | null =
    mode === "relative"
      ? Number.isInteger(n) && n >= 1 && n <= 3650
        ? { mode, days: n }
        : null
      : (() => {
          const at = dateStart(date);
          return at ? { mode, date: at } : null;
        })();
  const conditions = start ? JSON.stringify({ categories, artists, tags, xtags, start }) : "";
  const named = name.trim();

  useEffect(() => {
    if (!conditions) {
      setPreview({ state: "idle" });
      return;
    }
    let alive = true;
    const controller = new AbortController();
    setPreview({ state: "loading", matching: 0 });
    const timer = setTimeout(() => {
      // the name is not a condition; the contract only wants one
      const body = { ...(JSON.parse(conditions) as Omit<RuleInput, "name">), name: "미리 보기" };
      void (async () => {
        let cursor: string | null = "";
        let matching = 0;
        let notDownloaded = 0;
        while (alive && cursor !== null) {
          const value: SuccessOf<RouteById<"admin.previewRule">> = await client.call("admin.previewRule", {
            body: { ...body, cursor },
            signal: controller.signal,
          });
          if (!alive) return;
          if (value.matching === null || value.notDownloaded === null) {
            setPreview({ state: "ready", matching: null, notDownloaded: null });
            return;
          }
          matching += value.matching;
          notDownloaded += value.notDownloaded;
          cursor = value.nextCursor ?? null;
          setPreview(
            cursor === null ? { state: "ready", matching, notDownloaded } : { state: "loading", matching },
          );
        }
      })().catch((error: unknown) => alive && setPreview({ state: "error", error }));
    }, 300);
    return () => {
      alive = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [client, conditions]);

  const needle = typed.trim();
  useEffect(() => {
    const tag = needle.replace(/^[+-]/, "");
    if (!tag) {
      setSuggestions([]);
      return;
    }
    let alive = true;
    const timer = setTimeout(() => {
      client.call("catalog.suggestTags", { query: { q: tag.slice(0, 100), limit: 10 } }).then(
        (res) =>
          alive && setSuggestions(res.items.map((i) => `${needle.startsWith("-") ? "-" : ""}${i.tag}`)),
        () => alive && setSuggestions([]),
      );
    }, 150);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [client, needle]);

  function addTags(exclude = false) {
    if (!needle) return;
    try {
      const next = addRuleTags(tags, xtags, needle, exclude);
      setTags(next.tags);
      setXtags(next.xtags);
      setTyped("");
      setArtistError(null);
      return next;
    } catch (error) {
      setArtistError((error as Error).message);
      return null;
    }
  }

  async function act<T>(call: () => Promise<T>, after: (value: T) => void) {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      after(await call());
    } catch (e) {
      setError(e);
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  function save(e: FormEvent) {
    e.preventDefault();
    if (!start || !named || locked) return;
    const next = needle ? addTags() : { tags, xtags };
    if (!next) return;
    const body: RuleInput = { name: named, categories, artists, ...next, start };
    void act(
      () =>
        rule
          ? client.call("admin.updateRule", { params: { id: rule.id }, body })
          : client.call("admin.createRule", { body }),
      (saved) => {
        onSaved(saved);
        setDone(rule ? "저장했습니다." : "만들었습니다. 켜면 시작 시점부터 받습니다.");
      },
    );
  }

  return (
    <OpsDrawer
      title={rule ? `규칙 · ${rule.name}` : "새 규칙"}
      onClose={onClose}
      footer={
        rule ? (
          <>
            <Button
              variant={rule.enabled ? "plain" : "primary"}
              disabled={busy}
              onClick={() =>
                void act(
                  () =>
                    client.call("admin.setRuleEnabled", {
                      params: { id: rule.id },
                      body: { enabled: !rule.enabled },
                    }),
                  onSaved,
                )
              }
            >
              {rule.enabled ? "끄기" : "켜기"}
            </Button>
            <Button
              disabled={busy || locked || rule.stats.queued === 0}
              title={locked ? "끈 뒤 취소할 수 있습니다" : undefined}
              onClick={() => setAsking("cancel")}
            >
              대기 취소({formatCount(rule.stats.queued)})
            </Button>
            <span className="ops-r" />
            <Button
              type="submit"
              form={formId}
              disabled={busy || locked || !start || !named}
              title={locked ? "끈 뒤 고칠 수 있습니다" : undefined}
            >
              저장
            </Button>
            <Button
              disabled={busy || locked}
              title={locked ? "끈 뒤 지울 수 있습니다" : undefined}
              onClick={() => setAsking("delete")}
            >
              삭제
            </Button>
          </>
        ) : (
          <Button type="submit" form={formId} variant="primary" disabled={busy || !start || !named}>
            만들기
          </Button>
        )
      }
    >
      {rule && (
        <article className="ops-rule" data-enabled={rule.enabled} aria-label="규칙 상태">
          <p>
            <span className="ops-dot" data-tone={rule.enabled ? "run" : "idle"} />
            {PHASE_LABELS[rule.phase]}
          </p>
          <p className="note">
            {rule.enabled && rule.effectiveFrom
              ? `${formatDateTime(rule.effectiveFrom)} 이후 게시작${rule.enabledAt ? ` · ${formatDateTime(rule.enabledAt)}에 켬` : ""}`
              : "꺼져 있음 · 켜면 시작 시점부터 다시 넣습니다(이미 받은 것은 건너뜀)"}
          </p>
          <dl className="ops-rule-stats">
            <div>
              <dt>대기</dt>
              <dd>{formatCount(rule.stats.queued)}</dd>
            </div>
            <div>
              <dt>받는 중</dt>
              <dd>{formatCount(rule.stats.running)}</dd>
            </div>
            <div>
              <dt>받음</dt>
              <dd>{formatCount(rule.stats.done)}</dd>
            </div>
            <div>
              <dt>실패</dt>
              <dd data-bad={rule.stats.failed > 0}>{formatCount(rule.stats.failed)}</dd>
            </div>
          </dl>
          <Button variant="text" onClick={() => onShowDownloads(rule.id)}>
            이 규칙의 다운로드 보기
          </Button>
        </article>
      )}
      {error !== null && (
        <p className="note note-warn" role="alert">
          {describeError(error, RULE_ERRORS)}
        </p>
      )}
      {done && (
        <p className="note" role="status">
          {done}
        </p>
      )}
      {locked && (
        <p className="ops-memo">
          켜져 있는 동안에는 조건을 바꾸거나 대기를 취소할 수 없습니다. 끄면 아래 칸을 고치고 대기를 취소할 수
          있습니다.
        </p>
      )}
      <form id={formId} className="ops-edit" onSubmit={save}>
        <fieldset disabled={locked}>
          <legend className="sr">조건</legend>
          <label className="field">
            이름
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              required
              placeholder="예: 동인지 최근 7일"
            />
          </label>
          <div className="field">
            <span>분류</span>
            <CategoryFilter
              selected={categories}
              onToggle={(c) =>
                setCategories(categories.includes(c) ? categories.filter((x) => x !== c) : [...categories, c])
              }
            />
            <span className="note">하나도 안 고르면 모든 분류. 여러 개면 그중 하나라도 맞으면 받습니다.</span>
          </div>
          <div className="field">
            <label htmlFor={`${formId}-tags`}>태그</label>
            {artists.length > 0 && (
              <div className="ops-legacy-artists">
                <span className="note">기존 작가 조건 · 다음 작가 중 하나 포함</span>
                <div className="ops-chips">
                  {artists.map((tag) => (
                    <Token
                      key={tag}
                      namespace="artist"
                      removeLabel={`기존 작가 ${tag.slice(7)} 빼기`}
                      onRemove={() => setArtists(artists.filter((a) => a !== tag))}
                    >
                      {tag.slice(7)}
                    </Token>
                  ))}
                </div>
              </div>
            )}
            <div className="ops-tag-field">
              {[
                ...tags.map((tag) => ({ tag, excluded: false })),
                ...xtags.map((tag) => ({ tag, excluded: true })),
              ].map(({ tag, excluded }) => (
                <Token
                  key={tag}
                  excluded={excluded}
                  removeLabel={`${excluded ? "제외" : "포함"} ${tag} 빼기`}
                  onRemove={() =>
                    excluded
                      ? setXtags(xtags.filter((t) => t !== tag))
                      : setTags(tags.filter((t) => t !== tag))
                  }
                >
                  {excluded ? "−" : "+"}
                  {tag}
                </Token>
              ))}
              <input
                id={`${formId}-tags`}
                className="bar-input"
                value={typed}
                onChange={(e) => {
                  setTyped(e.target.value);
                  setArtistError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!e.nativeEvent.isComposing && e.keyCode !== 229) addTags(e.shiftKey);
                  }
                }}
                list={listId}
                placeholder="+language:korean +artist:이름 -other:anthology"
                maxLength={10000}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                aria-invalid={artistError !== null}
                aria-describedby={`${formId}-tag-help`}
              />
              <Button onClick={() => addTags()} disabled={!needle}>
                태그 넣기
              </Button>
            </div>
            <datalist id={listId}>
              {suggestions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            {artistError && (
              <span className="note note-warn" role="alert">
                {artistError}
              </span>
            )}
            <span className="note" id={`${formId}-tag-help`}>
              +는 모두 포함, -는 제외. 여러 태그를 한 번에 입력하고 Enter로 넣으세요. Shift+Enter는
              제외합니다. 비우면 태그 제한 없음.
            </span>
          </div>
          <div className="field">
            <Segmented
              legend="언제부터 올라온 작품"
              options={START_MODES}
              isOn={(v) => v === mode}
              onPick={setMode}
            />
            {mode === "relative" ? (
              <label className="ops-add-row">
                <input
                  className="input ops-days"
                  type="number"
                  min={1}
                  max={3650}
                  step={1}
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  aria-label="며칠 전부터"
                  aria-invalid={!start}
                />
                <span className="note">일 전부터 · 켜는 순간 기준</span>
              </label>
            ) : (
              <label className="ops-add-row">
                <input
                  className="input"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  aria-label="시작 날짜"
                  aria-invalid={!start}
                />
                <span className="note">이날 0시 이후 게시작</span>
              </label>
            )}
            <span className="note">
              {start
                ? "켜면 그 시점부터 지금까지를 모두 넣고, 켜 둔 동안 새로 수집되는 작품을 계속 넣습니다."
                : mode === "relative"
                  ? "1~3650일 사이의 정수를 넣으세요."
                  : "날짜를 고르세요."}
            </span>
          </div>
        </fieldset>
      </form>
      <section className="ops-preview" aria-label="미리 보기" aria-live="polite">
        <h3 className="ops-h3">미리 보기</h3>
        {preview.state === "loading" && (
          <p className="note">
            계산 중…{preview.matching > 0 ? ` ${formatCount(preview.matching)}개 확인` : ""}
          </p>
        )}
        {preview.state === "error" && <p className="note note-warn">{describeError(preview.error)}</p>}
        {preview.state === "ready" &&
          (preview.matching === null || preview.notDownloaded === null ? (
            <p className="note-warn">계산 중 시간 초과</p>
          ) : (
            <dl className="ops-rule-stats">
              <div>
                <dt>조건에 맞는 작품</dt>
                <dd data-preview="matching">{formatCount(preview.matching)}</dd>
              </div>
              <div>
                <dt>아직 안 받은 것(켜면 넣음)</dt>
                <dd data-preview="not-downloaded">{formatCount(preview.notDownloaded)}</dd>
              </div>
            </dl>
          ))}
        <p className="note">하드 차단·삭제된 작품과 규칙에서 실패한 작품은 빼고 셉니다.</p>
      </section>
      {rule && asking === "cancel" && (
        <ConfirmDialog
          title={`대기 ${formatCount(rule.stats.queued)}개를 취소할까요?`}
          confirmLabel="대기 취소"
          onCancel={() => setAsking(null)}
          returnFocus={drawerOf}
          onConfirm={() => {
            setAsking(null);
            void act(
              () => client.call("admin.cancelRuleQueue", { params: { id: rule.id } }),
              ({ cancelled }) => {
                setDone(`대기 ${formatCount(cancelled)}개를 취소했습니다.`);
                onChanged();
              },
            );
          }}
        >
          <p>
            ‘{rule.name}’ 규칙이 넣고 아직 슬롯을 받지 않은 작품을 대기열에서 뺍니다. 웹이나 봇에서도 요청한
            작품은 남습니다.
          </p>
        </ConfirmDialog>
      )}
      {rule && asking === "delete" && (
        <ConfirmDialog
          title={`‘${rule.name}’ 규칙을 지울까요?`}
          confirmLabel="삭제"
          onCancel={() => setAsking(null)}
          returnFocus={drawerOf}
          onConfirm={() => {
            setAsking(null);
            void act(
              () => client.call("admin.deleteRule", { params: { id: rule.id } }),
              () => onDeleted(rule.id),
            );
          }}
        >
          <p>이미 받은 작품과 대기 중인 작품은 그대로 둡니다. 되돌릴 수 없습니다.</p>
        </ConfirmDialog>
      )}
    </OpsDrawer>
  );
}
